import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, Warehouse, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface Warehouse {
  id: string;
  warehouseCode: string;
  name: string;
  locationId: string | null;
  status: string;
  notes: string | null;
  tempZones: string[];
}

interface Location {
  id: string;
  name: string;
  city: string | null;
}

export default function WarehousesSettingsPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    warehouseCode: "", name: "", status: "active",
    notes: "", tempZones: [] as string[], locationId: null as string | null,
  });

  const { data: warehouses = [] } = useQuery<Warehouse[]>({
    queryKey: ["/api/warehouses"],
    queryFn: () => api.get<Warehouse[]>(`/warehouses`, sessionId),
  });

  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
    queryFn: () => api.get<Location[]>(`/locations`, sessionId),
  });

  const saveMutation = useMutation({
    mutationFn: () => editingId
      ? api.patch(`/warehouses/${editingId}`, form, sessionId)
      : api.post(`/warehouses`, form, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/warehouses"] });
      setDialogOpen(false);
      setEditingId(null);
      toast({ title: editingId ? "Warehouse updated" : "Warehouse created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/warehouses/${id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/warehouses"] });
      toast({ title: "Warehouse deleted" });
    },
  });

  const handleEdit = (w: Warehouse) => {
    setEditingId(w.id);
    setForm({
      warehouseCode: w.warehouseCode,
      name: w.name,
      status: w.status,
      notes: w.notes || "",
      tempZones: w.tempZones || [],
      locationId: w.locationId,
    });
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setForm({ warehouseCode: "", name: "", status: "active", notes: "", tempZones: [], locationId: null });
    setDialogOpen(true);
  };

  const handleDelete = (w: Warehouse) => {
    if (confirm(`Delete warehouse "${w.name}"?`)) {
      deleteMutation.mutate(w.id);
    }
  };

  const statusVariant = (s: string): "default" | "secondary" | "outline" => {
    if (s === "active") return "default";
    if (s === "maintenance") return "secondary";
    return "outline";
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Warehouses"
        description="Manage your warehouse locations"
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={handleNew} data-testid="button-new-warehouse">
              <Plus className="h-4 w-4 mr-1" /> Add Warehouse
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Warehouse className="h-4 w-4" /> All Warehouses ({warehouses.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {warehouses.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No warehouses configured.</p>
          ) : (
            <div className="space-y-2">
              {warehouses.map((w) => (
                <div
                  key={w.id}
                  className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/40"
                  data-testid={`warehouse-row-${w.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-medium text-muted-foreground">{w.warehouseCode}</span>
                      <span className="font-medium text-sm">{w.name}</span>
                      <Badge variant={statusVariant(w.status)} className="text-[10px] capitalize">{w.status}</Badge>
                    </div>
                    {w.tempZones && w.tempZones.length > 0 && (
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        <span className="text-xs text-muted-foreground">Zones:</span>
                        {w.tempZones.map(z => (
                          <Badge key={z} variant="outline" className="text-[10px]">{z}</Badge>
                        ))}
                      </div>
                    )}
                    {w.notes && <p className="text-xs text-muted-foreground mt-1">{w.notes}</p>}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(w)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(w)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Warehouse" : "New Warehouse"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Code *</label>
                <Input
                  value={form.warehouseCode}
                  onChange={(e) => setForm({ ...form, warehouseCode: e.target.value })}
                  placeholder="WH-001"
                  disabled={!!editingId}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Name *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Main Distribution Center"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Location</label>
              <Select
                value={form.locationId || "none"}
                onValueChange={(v) => setForm({ ...form, locationId: v === "none" ? null : v })}
              >
                <SelectTrigger><SelectValue placeholder="Select location..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No location</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}{loc.city ? ` - ${loc.city}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Temperature Zones (comma separated)</label>
              <Input
                value={(form.tempZones || []).join(", ")}
                onChange={(e) => setForm({ ...form, tempZones: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                placeholder="Ambient, Refrigerated, Frozen"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Additional notes..."
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={!form.warehouseCode || !form.name || saveMutation.isPending}
              >
                {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingId ? "Save Changes" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
